// src/pages/customer-panel/invoices/InvoicesPage.js
// Invoice Listing Page

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    InputAdornment,
    Tooltip,
    Tabs,
    Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const STATUS_CONFIG = {
    paid: { color: '#10b981', bg: '#d1fae5', label: 'Paid' },
    unpaid: { color: '#f59e0b', bg: '#fef3c7', label: 'Unpaid' },
    void: { color: '#6b7280', bg: '#f3f4f6', label: 'Void' },
};

const STATUS_TABS = ['all', 'unpaid', 'paid', 'void'];

export default function InvoicesPage() {
    const navigate = useNavigate();

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                search,
                page: page + 1,
                limit: rowsPerPage,
            };
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            const response = await axiosInstance.get('/invoices', { params });
            const data = response.data;
            setInvoices(data.data || data.invoices || []);
            setTotalCount(data.total || data.totalCount || 0);
        } catch (err) {
            console.error('Failed to fetch invoices:', err);
            setError(err.response?.data?.message || 'Failed to load invoices.');
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, page, rowsPerPage]);

    useEffect(() => {
        document.title = 'Invoices | Customer Panel';
        fetchInvoices();
    }, [fetchInvoices]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return '--';
        }
    };

    const formatCurrency = (amount) => {
        if (amount == null) return '--';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const getStatusChip = (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.unpaid;
        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: config.bg,
                    color: config.color,
                }}
            />
        );
    };

    const handleRowClick = (invoice) => {
        const id = invoice.id || invoice._id;
        navigate(`/customer-panel/invoices/${id}`);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Invoices
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                        View and track your invoices
                    </Typography>
                </Box>
                <IconButton onClick={fetchInvoices} sx={{ color: '#64748b' }}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            )}

            <Paper
                sx={{
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                }}
            >
                {/* Status Tabs */}
                <Tabs
                    value={statusFilter}
                    onChange={(e, newVal) => { setStatusFilter(newVal); setPage(0); }}
                    sx={{
                        px: 2,
                        borderBottom: '1px solid',
                        borderColor: 'grey.100',
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            minHeight: 48,
                        },
                        '& .Mui-selected': { color: GREEN_ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: GREEN_ACCENT },
                    }}
                >
                    {STATUS_TABS.map((tab) => (
                        <Tab
                            key={tab}
                            label={tab.charAt(0).toUpperCase() + tab.slice(1)}
                            value={tab}
                        />
                    ))}
                </Tabs>

                {/* Search */}
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <TextField
                        placeholder="Search invoices..."
                        size="small"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        sx={{
                            flex: 1,
                            maxWidth: 400,
                            '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchInvoices} sx={{ color: '#64748b' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                {['Invoice #', 'Customer', 'Amount', 'Status', 'Date', 'Due Date', ''].map((header) => (
                                    <TableCell
                                        key={header}
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            color: '#64748b',
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                                    </TableCell>
                                </TableRow>
                            ) : invoices.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                        <ReceiptIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No invoices found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                invoices.map((invoice) => (
                                    <TableRow
                                        key={invoice.id || invoice._id}
                                        onClick={() => handleRowClick(invoice)}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: '#f8fafc' },
                                        }}
                                    >
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: GREEN_ACCENT }}>
                                            {invoice.invoice_number || invoice.invoiceNumber || `INV-${(invoice.id || invoice._id || '').toString().slice(-6)}`}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                            {invoice.customer_name || invoice.customerName || invoice.customer?.name || '--'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                                            {formatCurrency(invoice.amount || invoice.total)}
                                        </TableCell>
                                        <TableCell>
                                            {getStatusChip(invoice.status)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                            {formatDate(invoice.created_on || invoice.created_at || invoice.date)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                            {formatDate(invoice.due_on || invoice.due_date || invoice.dueDate)}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" sx={{ color: '#94a3b8' }}>
                                                <ArrowForwardIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ '& *': { fontFamily } }}
                />
            </Paper>
        </Box>
    );
}
